import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import DraggableModal from "../DraggableModal";
import EmailDialog from "../EmailDialog";
import { htmlToPdfBlob, safeFilename } from "../../pdfRenderer";
import { mergePdfBlobs } from "../../pdfMerge";
import { loadDocMap } from "../gfiDB";
import { loadLS, escHtml, fmtDateShort, getCompanyName, reportStyles, reportHeader, reportFooter, openPreviewWindow, downloadBlob } from "./auditCommon";
import { loadFromSupabase } from "../../supabaseDB";
import { appendDisclosureByAccount } from "../emailHelpers";

/* ============================================================================
 * Organic Certificates Report
 *
 * Checkbox tree of every certification on file, grouped into four
 * sections: GFI / Shipper / Warehouse (service providers) / Growers.
 * Every cert is checked by default; the auditor (or Arturo) unchecks
 * the ones they don't want in the bundle.
 *
 * The PDF output is one combined file:
 *   page 1+: executive list of all selected certs (table)
 *   page N+: each selected cert's stored PDF document, in the same
 *            order as the executive list. PDFs come from IndexedDB
 *            (`cert-docs` doc map, keyed by cert id) and are merged
 *            via pdf-lib.
 *
 * If a cert has no PDF on file, it appears in the executive list but
 * skips the attachment slot — a small "no doc on file" note is added
 * in its row so the auditor knows why an annex is missing.
 * ========================================================================= */

const T = {
  en: {
    title: "Organic Certificates Report",
    sub: "Executive list + attached PDFs",
    selectionTitle: "Select certificates to include",
    selectAll: "Select all", deselectAll: "Deselect all",
    sec_gfi: "GFI (own certificate)",
    sec_shipper: "Shipper",
    sec_warehouse: "Warehouse / Cooler",
    sec_grower: "Growers",
    sec_other: "Other",
    col_entity: "Entity", col_owner: "Owner", col_nop: "NOP #",
    col_certNumber: "Certificate #", col_certifier: "Certifier",
    col_operation: "Operation Type", col_product: "Reported Product",
    col_farmName: "Farm / Rancho", col_farmAddress: "Farm Address",
    col_validFrom: "Valid From", col_validTo: "Valid Until",
    col_issued: "Issued",
    col_status: "Status",
    statusActive: "Active", statusExpired: "Expired",
    noDoc: "No PDF on file",
    docCount: "with PDF",
    btnPreview: "Preview list", btnPdf: "Print / PDF", btnEmail: "Email", close: "Close",
    sending: "Building combined PDF...",
    emailSubject: "Organic Certificates Report",
    empty: "No certifications match the selected groups.",
    selectedCount: "selected",
  },
  es: {
    title: "Reporte de Certificados Orgánicos",
    sub: "Lista ejecutiva + PDFs adjuntos",
    selectionTitle: "Selecciona los certificados a incluir",
    selectAll: "Seleccionar todo", deselectAll: "Deseleccionar todo",
    sec_gfi: "GFI (certificado propio)",
    sec_shipper: "Exportador",
    sec_warehouse: "Bodega / Frigorífico",
    sec_grower: "Productores",
    sec_other: "Otros",
    col_entity: "Entidad", col_owner: "Nombre del dueño", col_nop: "NOP #",
    col_certNumber: "Número de certificado", col_certifier: "Certificadora",
    col_operation: "Tipo de operación", col_product: "Producto reportado",
    col_farmName: "Nombre del rancho", col_farmAddress: "Domicilio del rancho",
    col_validFrom: "Vigente desde", col_validTo: "Vigente hasta",
    col_issued: "Fecha emisión",
    col_status: "Estado",
    statusActive: "Activo", statusExpired: "Vencido",
    noDoc: "Sin PDF cargado",
    docCount: "con PDF",
    btnPreview: "Vista previa", btnPdf: "Imprimir / PDF", btnEmail: "Enviar Correo", close: "Cerrar",
    sending: "Generando PDF combinado...",
    emailSubject: "Reporte de Certificados Orgánicos",
    empty: "No hay certificaciones que coincidan con los grupos.",
    selectedCount: "seleccionados",
  },
};

const btnPrimary = { padding: "8px 16px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 13, fontWeight: 700 };
const btnSecondary = { padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 500 };

/* Operation-type mapper — duplicated from growerAudit.js by intent so
   the file stays standalone. */
function operationLabel(subtype) {
  if (!subtype) return "";
  var st = String(subtype).toLowerCase();
  var map = {
    crops:           "Crops",
    handler_trader:  "Handler (Trader/Broker)",
    handler_cooling: "Handler (Cooling/Storage)",
    farm:            "Farm",
    harvest_crew:    "Harvest Crew",
    cooling:         "Cooling / Cold Storage",
    other:           "Other",
  };
  return map[st] || subtype;
}

/* Group a cert into one of {gfi, shipper, warehouse, grower, other}. */
function bucketFor(cert) {
  if (!cert) return "other";
  if (cert.entityType === "gfi") return "gfi";
  if (cert.entityType === "shipper") return "shipper";
  if (cert.entityType === "grower")  return "grower";
  if (cert.entityType === "service") return "warehouse";
  return "other";
}

/* Resolve an entity name from entityId. The CertificationRegistry has
   a richer version of this but we keep ours standalone so the report
   doesn't depend on UI internals. */
function entityNameFor(entityId, entityType, lookups) {
  if (!entityId) return "—";
  if (entityType === "gfi") return getCompanyName();
  if (entityType === "shipper") {
    var id = String(entityId).replace("shipper-", "");
    var s = (lookups.suppliers || []).find(function (x) { return String(x.id) === id; });
    return s ? (s.shortName || s.fullName || s.name || id) : id;
  }
  if (entityType === "grower") {
    var gid = String(entityId).replace("grower-", "");
    var g = (lookups.growers || []).find(function (x) { return String(x.id) === gid; });
    if (!g) return gid;
    if (g.personType === "moral") return g.razonSocial || gid;
    return [g.nombre, g.apPaterno, g.apMaterno].filter(Boolean).join(" ") || gid;
  }
  if (entityType === "service") {
    var sid = String(entityId).replace("service-", "");
    var w = (lookups.warehouses || []).find(function (x) { return String(x.id) === sid; });
    if (w) return w.shortName || w.fullName || w.name || sid;
    return sid;
  }
  return String(entityId);
}

/* Address formatter for cert.farmAddress (structured object) or the
   embedded address inside a grower rancho. */
function formatAddress(addr) {
  if (!addr) return "";
  if (typeof addr === "string") return addr;
  return [addr.street, addr.extNumber, addr.colonia, addr.city, addr.state, addr.postalCode, addr.country]
    .filter(Boolean).join(", ");
}

function getExpiry(cert) {
  return cert && (cert.anniversaryDate || cert.validTo) || "";
}
function isActive(cert) {
  var exp = getExpiry(cert);
  if (!exp) return true;
  var d = new Date(exp); d.setHours(23, 59, 59, 999);
  return d >= new Date();
}

/* Pull a PDF blob for a given cert from its documents list. We accept
   any document whose URL/data starts with "data:application/pdf" or
   whose name ends in .pdf. Returns null if none qualifies.

   Source priority (matches the rest of the app post-Storage-migration):
     1. d.url   — Supabase Storage public URL. Preferred when set.
     2. d.data  — legacy base64 data URL on pre-migration certs.
   Without the d.url branch, a cert whose doc was migrated to
   Storage (so d.data is gone, only d.url remains) silently produced
   no annex — and if the cert was multi-doc, the loop could even
   fetch an empty string and fall through. */
async function pdfBlobsForDocs(docList) {
  var out = [];
  var docs = docList || [];
  for (var i = 0; i < docs.length; i++) {
    var d = docs[i] || {};
    var name = String(d.name || "").toLowerCase();
    var src  = d.url || d.data || "";
    if (!src) continue;
    var looksPdf = (typeof src === "string" && (src.indexOf("data:application/pdf") === 0 || /\.pdf(\?|$)/i.test(src))) || name.endsWith(".pdf");
    if (!looksPdf) continue;
    try {
      var blob = await fetch(src).then(function (r) {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.blob();
      });
      out.push({ blob: blob, name: d.name || ((d.docType || "cert") + ".pdf") });
    } catch (e) {
      console.warn("[organic-certs] failed to fetch", (d.url ? "url" : "data"), ":", e && e.message);
    }
  }
  return out;
}

/* ============================================================================
 * HTML builder — executive list
 * ========================================================================= */
function buildCertListHTML(certsBySection, selectedIds, lookups, audit, lang) {
  var t = T[lang === "es" ? "es" : "en"];
  var SECTION_ORDER = ["gfi", "shipper", "warehouse", "grower", "other"];
  var SEC_LABELS = { gfi: t.sec_gfi, shipper: t.sec_shipper, warehouse: t.sec_warehouse, grower: t.sec_grower, other: t.sec_other };

  var h = '';
  h += '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + escHtml(t.title) + '</title>';
  h += '<style>' + reportStyles() + ' table th, table td { font-size: 9px; padding: 4px 6px } .commodity-title{font-size:12px}</style></head><body><div class="page">';
  h += reportHeader(t.title, "", "", audit);

  var grandTotal = 0;
  SECTION_ORDER.forEach(function (key) {
    var arr = (certsBySection[key] || []).filter(function (c) { return selectedIds[c.id]; });
    if (arr.length === 0) return;
    grandTotal += arr.length;
    h += '<div class="commodity-title">' + escHtml(SEC_LABELS[key]) + ' (' + arr.length + ')</div>';
    h += '<table><thead><tr>';
    h += '<th>' + escHtml(t.col_entity) + '</th>';
    h += '<th>' + escHtml(t.col_certNumber) + '</th>';
    h += '<th>' + escHtml(t.col_certifier) + '</th>';
    h += '<th>' + escHtml(t.col_operation) + '</th>';
    h += '<th>' + escHtml(t.col_product) + '</th>';
    h += '<th>' + escHtml(t.col_farmName) + '</th>';
    h += '<th>' + escHtml(t.col_validFrom) + '</th>';
    h += '<th>' + escHtml(t.col_validTo) + '</th>';
    h += '<th>' + escHtml(t.col_status) + '</th>';
    h += '</tr></thead><tbody>';
    arr.forEach(function (c) {
      var entName = entityNameFor(c.entityId, c.entityType, lookups);
      var certifier = c.certifier === "__other__" ? (c.certifierOther || "—") : (c.certifier || "—");
      var products = (c.products || []).map(function (p) { return p.productName || ""; }).filter(Boolean).join(", ");
      var op = operationLabel(c.subtype);
      var active = isActive(c);
      h += '<tr>';
      h += '<td>' + escHtml(entName) + '</td>';
      h += '<td style="font-family:\'JetBrains Mono\',monospace">' + escHtml(c.certNumber || "—") + (c.nopId ? '<div style="font-size:8px;color:#888">NOP: ' + escHtml(c.nopId) + '</div>' : '') + '</td>';
      h += '<td>' + escHtml(certifier) + '</td>';
      h += '<td>' + escHtml(op) + '</td>';
      h += '<td>' + escHtml(products || "—") + '</td>';
      h += '<td>' + escHtml(c.farmName || "—") + (c.farmAddress && formatAddress(c.farmAddress) ? '<div style="font-size:8px;color:#666">' + escHtml(formatAddress(c.farmAddress)) + '</div>' : '') + '</td>';
      h += '<td style="font-family:\'JetBrains Mono\',monospace">' + escHtml(fmtDateShort(c.validFrom) || "—") + '</td>';
      h += '<td style="font-family:\'JetBrains Mono\',monospace">' + escHtml(fmtDateShort(getExpiry(c)) || "—") + '</td>';
      var pill = active ? 'background:rgba(16,185,129,0.12);color:#047857;border:1px solid rgba(16,185,129,0.30)' : 'background:rgba(220,38,38,0.12);color:#991B1B;border:1px solid rgba(220,38,38,0.30)';
      h += '<td><span style="display:inline-block;padding:1px 6px;border-radius:8px;font-size:8px;font-weight:700;' + pill + '">' + escHtml(active ? t.statusActive : t.statusExpired) + '</span></td>';
      h += '</tr>';
    });
    h += '</tbody></table>';
  });

  if (grandTotal === 0) {
    h += '<div style="padding:24px;text-align:center;color:#888;font-style:italic;border:1px dashed #d1d5db;border-radius:4px">' + escHtml(t.empty) + '</div>';
  }
  h += reportFooter(audit, lang);
  h += '</div></body></html>';
  return h;
}

/* ============================================================================
 * Panel UI
 * ========================================================================= */
export default function OrganicCertsPanel({ lang, audit, onClose, onLogReport, initialParams }) {
  const t = T[lang === "es" ? "es" : "en"];
  /* Pull certifications + (asynchronously) their doc map. */
  const [certs] = useState(() => loadLS("gfi-certifications", []));
  const [docMap, setDocMap] = useState({});
  const [lookups] = useState(() => ({
    suppliers:  loadLS("gfi-suppliers", []),
    warehouses: loadLS("gfi-warehouses", []),
    growers:    loadLS("gfi-growers", []),
  }));
  const [busy, setBusy] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  /* Snapshot blob shared between buildAttachments + onSent so the
     registry stores exactly what the auditor received. */
  const lastBlobRef = useRef(null);
  const lastFilenameRef = useRef("");

  useEffect(() => {
    let cancelled = false;
    loadDocMap("cert-docs").then(function (m) { if (!cancelled) setDocMap(m || {}); });
    return function () { cancelled = true; };
  }, []);

  /* Limit to organic certs (certType === "organic") + the GFI cert
     itself. Food-safety certs are out of scope for this report. */
  const organicCerts = useMemo(() => {
    return (certs || []).filter(function (c) {
      if (c && c.certType === "organic") return true;
      if (c && c.entityType === "gfi") return true;
      return false;
    });
  }, [certs]);

  /* Group certs by section. */
  const certsBySection = useMemo(() => {
    const out = { gfi: [], shipper: [], warehouse: [], grower: [], other: [] };
    organicCerts.forEach(function (c) { out[bucketFor(c)].push(c); });
    /* Sort within each section by entity name for stable display. */
    Object.keys(out).forEach(function (k) {
      out[k].sort(function (a, b) {
        return entityNameFor(a.entityId, a.entityType, lookups).localeCompare(entityNameFor(b.entityId, b.entityType, lookups));
      });
    });
    return out;
  }, [organicCerts, lookups]);

  /* Selection state — every cert defaults to checked. Keyed by id.
     When `initialParams.selectedCertIds` is supplied (Reopen flow),
     restore that selection instead of "everything". */
  const [selected, setSelected] = useState({});
  useEffect(() => {
    const next = {};
    if (initialParams && Array.isArray(initialParams.selectedCertIds)) {
      initialParams.selectedCertIds.forEach(function (id) { next[id] = true; });
    } else {
      organicCerts.forEach(function (c) { next[c.id] = true; });
    }
    setSelected(next);
  }, [organicCerts]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setSelected(prev => ({ ...prev, [id]: !prev[id] }));

  /* Per-document selection. Key = cert.id + "|" + doc.id. Default:
     only the "Certificate" doc of each cert is checked. */
  const [selectedDocs, setSelectedDocs] = useState({});
  const docKey = (certId, docId) => String(certId) + "|" + String(docId);
  const toggleDoc = (certId, docId) => setSelectedDocs(prev => {
    const k = docKey(certId, docId);
    return { ...prev, [k]: !prev[k] };
  });
  const isDocChecked = (certId, doc) => {
    const k = docKey(certId, doc.id);
    if (selectedDocs.hasOwnProperty(k)) return selectedDocs[k];
    return doc.docType === "Certificate"; /* default */
  };
  const sectionAll = (key, value) => {
    setSelected(prev => {
      const next = { ...prev };
      (certsBySection[key] || []).forEach(function (c) { next[c.id] = value; });
      return next;
    });
  };

  /* Augment each cert record with its loaded documents for the PDF
     extraction step. We don't mutate the originals. */
  const certsWithDocs = useMemo(() => {
    return organicCerts.map(function (c) {
      var docs = docMap[c.id] || (Array.isArray(c.documents) ? c.documents : []);
      return { ...c, _docs: docs };
    });
  }, [organicCerts, docMap]);

  const certsBySectionAug = useMemo(() => {
    const out = { gfi: [], shipper: [], warehouse: [], grower: [], other: [] };
    certsWithDocs.forEach(function (c) { out[bucketFor(c)].push(c); });
    Object.keys(out).forEach(function (k) {
      out[k].sort(function (a, b) { return entityNameFor(a.entityId, a.entityType, lookups).localeCompare(entityNameFor(b.entityId, b.entityType, lookups)); });
    });
    return out;
  }, [certsWithDocs, lookups]);

  const selectedCount = useMemo(() => Object.keys(selected).filter(function (k) { return selected[k]; }).length, [selected]);
  const totalCount = organicCerts.length;

  const buildExecHtml = useCallback(() => buildCertListHTML(certsBySectionAug, selected, lookups, audit, lang), [certsBySectionAug, selected, lookups, audit, lang]);

  /* Build the combined PDF: executive list page → each selected cert's
     PDF appended. */
  const buildCombinedPdf = useCallback(async () => {
    /* Render the executive list page first. */
    /* `emptyPageThreshold: 0.15` — be aggressive about skipping a
       trailing nearly-empty page on the exec list. The default
       0.04 occasionally let through a sheet with only the table's
       footer-border on it. */
    const execBlob = await htmlToPdfBlob(buildExecHtml(), { emptyPageThreshold: 0.15 });
    const items = [{ name: "exec_list", blob: execBlob }];
    /* Iterate sections in stable order so the annex order matches the
       list order. */
    const SECTION_ORDER = ["gfi", "shipper", "warehouse", "grower", "other"];
    for (var i = 0; i < SECTION_ORDER.length; i++) {
      const key = SECTION_ORDER[i];
      const arr = (certsBySectionAug[key] || []).filter(function (c) { return selected[c.id]; });
      for (var j = 0; j < arr.length; j++) {
        const c = arr[j];
        const chosenDocs = (c._docs || []).filter(function (d) { return isDocChecked(c.id, d); });
        const pdfs = await pdfBlobsForDocs(chosenDocs);
        pdfs.forEach(function (p, idx) { items.push({ name: "cert_" + c.id + "_" + idx, blob: p.blob }); });
      }
    }
    /* skipBlankPages: user-uploaded cert PDFs sometimes carry
       trailing whitespace pages (multi-page scans where only the
       first sheet has the certificate). Strip them so the combined
       annex pack doesn't balloon to 2–3× its useful size. */
    return mergePdfBlobs(items, { skipBlankPages: true });
  }, [buildExecHtml, certsBySectionAug, selected, selectedDocs]);

  const handlePreview = () => openPreviewWindow(buildExecHtml());

  /* Save the actual checked-id list so Reopen can restore it. */
  const currentParams = useMemo(
    () => ({ selectedCertIds: Object.keys(selected).filter(function (k) { return selected[k]; }) }),
    [selected]
  );

  /* Build individual attachments: the executive list as one PDF +
     each checked document as its own file (no merge → no blank-page
     issues from pdf-lib copyPages on scanned certs). */
  const buildIndividualAttachments = useCallback(async () => {
    const out = [];
    const execBlob = await htmlToPdfBlob(buildExecHtml(), { emptyPageThreshold: 0.15 });
    out.push({ filename: safeFilename("Organic_Certificates_List_" + (audit && audit.name || "")) + ".pdf", blob: execBlob, mimeType: "application/pdf" });
    const SECTION_ORDER = ["gfi", "shipper", "warehouse", "grower", "other"];
    for (var i = 0; i < SECTION_ORDER.length; i++) {
      const arr = (certsBySectionAug[SECTION_ORDER[i]] || []).filter(function (c) { return selected[c.id]; });
      for (var j = 0; j < arr.length; j++) {
        const c = arr[j];
        const chosen = (c._docs || []).filter(function (d) { return isDocChecked(c.id, d); });
        for (var k = 0; k < chosen.length; k++) {
          const d = chosen[k];
          const src = d.url || d.data || "";
          if (!src) continue;
          try {
            const blob = await fetch(src).then(function (r) { if (!r.ok) throw new Error("HTTP " + r.status); return r.blob(); });
            out.push({ filename: safeFilename(d.name || ("cert_" + c.id)) + (/\.pdf$/i.test(d.name || "") ? "" : ".pdf"), blob: blob, mimeType: "application/pdf" });
          } catch (e) {
            console.warn("[OrganicCerts] failed to fetch doc", d.name, e && e.message);
          }
        }
      }
    }
    return out;
  }, [buildExecHtml, certsBySectionAug, selected, selectedDocs, audit]);

  const handlePdf = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const atts = await buildIndividualAttachments();
      atts.forEach(function (a) { downloadBlob(a.blob, a.filename); });
      onLogReport({
        type: "organic_certs",
        title: (lang === "es" ? "Documentos descargados" : "Documents downloaded") + " · " + atts.length,
        params: currentParams,
      });
    } catch (e) {
      console.error("[OrganicCerts] pdf failed", e);
      window.alert((lang === "es" ? "Error: " : "Error: ") + (e.message || String(e)));
    } finally { setBusy(false); }
  };

  const [emailProfile, setEmailProfile] = useState({});
  useEffect(() => {
    let alive = true;
    loadFromSupabase("company_profile")
      .then(function (rows) {
        if (!alive) return;
        var p = (rows || []).find(function (r) { return String(r.id) === "gfi"; }) || (rows || [])[0] || {};
        setEmailProfile(p);
      })
      .catch(function () {});
    return function () { alive = false; };
  }, []);
  const profile = emailProfile;
  const signatures = (profile && profile.emailSignatures) || [];
  const fromEmail  = (profile && (profile.outboundEmail || profile.email)) || "";
  /* 2026-09-30 — excludes Sweetleaf/automated accounts, same fix as
     OrganicAuditRegistry.js's own emailAccounts. */
  const emailAccounts = ((profile && profile.emailAccounts) || []).filter(function (a) { var n = (a.name || "").toLowerCase(); return n.indexOf("sweetleaf") < 0 && n.indexOf("automat") < 0; });

  const sectionRender = (key, label) => {
    const arr = certsBySectionAug[key] || [];
    if (arr.length === 0) return null;
    const selectedInSec = arr.filter(function (c) { return selected[c.id]; }).length;
    const allChecked = selectedInSec === arr.length;
    return (
      <div key={key} style={{ marginBottom: 14, border: "1px solid var(--color-border)", borderRadius: 6, overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "var(--color-bg)", borderBottom: "1px solid var(--color-border)" }}>
          <div style={{ fontWeight: 700, fontSize: 12, color: "var(--color-text)" }}>{label} <span style={{ color: "var(--color-text-muted)", fontWeight: 400 }}>({selectedInSec}/{arr.length})</span></div>
          <button onClick={() => sectionAll(key, !allChecked)} style={{ background: "none", border: "none", color: "var(--color-accent)", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>{allChecked ? t.deselectAll : t.selectAll}</button>
        </div>
        <div>
          {arr.map(function (c) {
            const entName = entityNameFor(c.entityId, c.entityType, lookups);
            const pdfCount = (c._docs || []).filter(function (d) {
              var nm = String(d && d.name || "").toLowerCase();
              return (d && typeof d.data === "string" && d.data.indexOf("data:application/pdf") === 0) || nm.endsWith(".pdf");
            }).length;
            return (
              <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 12px", borderBottom: "1px solid var(--color-border)", fontSize: 12, cursor: "pointer" }}>
                <input type="checkbox" checked={!!selected[c.id]} onChange={() => toggle(c.id)} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{entName}</div>
                  <div style={{ fontSize: 10, color: "var(--color-text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>
                    {(c.certNumber || "—")}{c.certifier && c.certifier !== "__other__" ? " · " + c.certifier : (c.certifierOther ? " · " + c.certifierOther : "")}
                    {operationLabel(c.subtype) ? " · " + operationLabel(c.subtype) : ""}
                  </div>
                </div>
                <div style={{ fontSize: 10, color: pdfCount > 0 ? "#047857" : "#DC2626", fontWeight: 700 }}>
                  {pdfCount > 0 ? "PDF" : t.noDoc}
                </div>
              </label>
            );
          })}
          {arr.filter(function (c) { return selected[c.id]; }).map(function (c) {
            var entName = entityNameFor(c.entityId, c.entityType, lookups);
            var docsWithPdf = (c._docs || []).filter(function (d) {
              var nm = String(d && d.name || "").toLowerCase();
              var src = (d && (d.url || d.data)) || "";
              return src && (/\.pdf(\?|$)/i.test(String(src)) || (typeof d.data === "string" && d.data.indexOf("data:application/pdf") === 0) || nm.endsWith(".pdf"));
            });
            if (docsWithPdf.length === 0) return null;
            return (
              <div key={"docs-" + c.id} style={{ padding: "6px 12px 8px 34px", background: "rgba(0,0,0,0.015)", borderBottom: "1px solid var(--color-border)" }}>
                <div style={{ fontSize: 9.5, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{entName} — {lang === "es" ? "documentos a anexar" : "documents to attach"}</div>
                {docsWithPdf.map(function (d) {
                  var dtl = (t.docTypeLabels && t.docTypeLabels[d.docType]) || d.docType || d.name || "—";
                  return (
                    <label key={c.id + "|" + d.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 0", fontSize: 11, cursor: "pointer" }}>
                      <input type="checkbox" checked={isDocChecked(c.id, d)} onChange={function () { toggleDoc(c.id, d.id); }} />
                      <span style={{ fontWeight: d.docType === "Certificate" ? 700 : 400 }}>{dtl}</span>
                      <span style={{ color: "var(--color-text-muted)", fontSize: 9.5 }}>{d.name}</span>
                    </label>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <DraggableModal title={t.title + " · " + (audit && audit.name || "")} onClose={onClose} zIndex={10003}>
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.selectionTitle}</div>
          <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{selectedCount} / {totalCount} {t.selectedCount}</div>
        </div>
        {totalCount === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: "var(--color-text-muted)", fontStyle: "italic", border: "1px dashed var(--color-border)", borderRadius: 8 }}>{t.empty}</div>
        ) : (
          <>
            {sectionRender("gfi",       t.sec_gfi)}
            {sectionRender("shipper",   t.sec_shipper)}
            {sectionRender("warehouse", t.sec_warehouse)}
            {sectionRender("grower",    t.sec_grower)}
            {sectionRender("other",     t.sec_other)}
          </>
        )}
      </div>
      <div style={{ padding: "12px 22px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{busy ? t.sending : ""}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} style={btnSecondary}>{t.close}</button>
          <button onClick={handlePreview} style={btnSecondary}>{t.btnPreview}</button>
          <button onClick={() => setShowEmail(true)} style={btnSecondary} disabled={selectedCount === 0}>{t.btnEmail}</button>
          <button onClick={handlePdf} style={btnPrimary} disabled={busy || selectedCount === 0}>{t.btnPdf}</button>
        </div>
      </div>

      {showEmail && (
        <EmailDialog
          lang={lang}
          title={t.btnEmail}
          recipients={(audit && audit.auditorEmail) ? [{ name: audit.auditorName || "", email: audit.auditorEmail, checked: true }] : []}
          docOptions={[{ key: "report", label: t.title, checked: true }]}
          defaultSubject={t.emailSubject + " — " + (audit && audit.name || "")}
          defaultBody={lang === "es"
            ? "Adjunto el PDF combinado de los certificados orgánicos seleccionados para la auditoría " + (audit && audit.name || "") + "."
            : "Attached is the combined PDF of the selected organic certificates for audit " + (audit && audit.name || "") + "."}
          fromEmail={fromEmail}
          emailAccounts={emailAccounts}
          signatures={signatures}
          postProcessHtmlBody={(html, resolvedFrom) => appendDisclosureByAccount(html, resolvedFrom, profile, null)}
          buildAttachments={async () => {
            return await buildIndividualAttachments();
          }}
          onSent={() => {
            const to = (audit && audit.auditorEmail) ? [audit.auditorEmail] : [];
            onLogReport({
              type: "organic_certs",
              title: (lang === "es" ? "Enviado por correo · " : "Sent by email · ") + selectedCount + " " + t.docCount,
              recipients: to,
              params: currentParams,
              blob: lastBlobRef.current, filename: lastFilenameRef.current, mime: "application/pdf",
            });
            setShowEmail(false);
          }}
          onClose={() => setShowEmail(false)}
        />
      )}
    </DraggableModal>
  );
}
